import { CLASSES, CLASS_IDS, type ClassId } from './classes';

/**
 * Earned once a lifetime stat reaches `at`. Only rated matches count. The lobby keeps the stats
 * (`server/src/db.ts`): games, wins, `wins:<class>` (both of a match's classes), `streak:win`, `streak:loss`, damage,
 * healing, and rating (the best a player has reached in any bracket).
 */
export interface AchievementDef {
  name: string;
  description: string;
  stat: string;
  at: number;
  /** Shown with the player's name once earned and chosen. */
  title?: string;
  /** A weapon trail in this class's color, which everyone sees once earned and chosen. */
  trail?: ClassId;
}

const CLASS_TIERS = [
  [50, 'Adept'],
  [100, 'Expert'],
  [250, 'Master'],
  [500, 'Grandmaster'],
] as const;

const classAchievements = Object.fromEntries(
  CLASS_IDS.flatMap((id) => {
    const name = CLASSES[id].name;
    return CLASS_TIERS.map(([at, rank]) => [
      `${id}-wins-${at}`,
      {
        name: `${name} ${rank}`,
        description: `Win ${at} rated matches with ${name} as one of your classes.`,
        stat: `wins:${id}`,
        at,
        ...(at === 500 ? { trail: id } : {}),
      },
    ]);
  }),
);

export const ACHIEVEMENTS: Record<string, AchievementDef> = {
  'first-win': {
    name: 'First Blood',
    description: 'Win a rated match.',
    stat: 'wins',
    at: 1,
  },
  'wins-25': {
    name: 'Contender',
    description: 'Win 25 rated matches.',
    stat: 'wins',
    at: 25,
    title: 'Contender',
  },
  'wins-100': {
    name: 'Champion',
    description: 'Win 100 rated matches.',
    stat: 'wins',
    at: 100,
    title: 'Champion',
  },
  'games-100': {
    name: 'Veteran',
    description: 'Play 100 rated matches.',
    stat: 'games',
    at: 100,
    title: 'Veteran',
  },
  'games-500': {
    name: 'Old Guard',
    description: 'Play 500 rated matches.',
    stat: 'games',
    at: 500,
    title: 'Old Guard',
  },
  'streak-5': {
    name: 'On a Roll',
    description: 'Win 5 rated matches in a row.',
    stat: 'streak:win',
    at: 5,
  },
  'streak-10': {
    name: 'Unstoppable',
    description: 'Win 10 rated matches in a row.',
    stat: 'streak:win',
    at: 10,
    title: 'the Unstoppable',
  },
  'loss-streak-5': {
    name: 'Character Building',
    description: 'Lose 5 rated matches in a row.',
    stat: 'streak:loss',
    at: 5,
    title: 'the Persistent',
  },
  'loss-streak-10': {
    name: 'Glutton for Punishment',
    description: 'Lose 10 rated matches in a row.',
    stat: 'streak:loss',
    at: 10,
    title: 'Glutton for Punishment',
  },
  'damage-100k': {
    name: 'Bruiser',
    description: 'Deal 100,000 damage in rated matches.',
    stat: 'damage',
    at: 100_000,
  },
  'damage-1m': {
    name: 'Destroyer',
    description: 'Deal 1,000,000 damage in rated matches.',
    stat: 'damage',
    at: 1_000_000,
    title: 'the Destroyer',
  },
  'healing-100k': {
    name: 'Mender',
    description: 'Heal 100,000 in rated matches.',
    stat: 'healing',
    at: 100_000,
  },
  'healing-1m': {
    name: 'Lifebinder',
    description: 'Heal 1,000,000 in rated matches.',
    stat: 'healing',
    at: 1_000_000,
    title: 'Lifebinder',
  },
  'rating-1600': {
    name: 'Challenger',
    description: 'Reach a rating of 1600 in any bracket.',
    stat: 'rating',
    at: 1600,
    title: 'Challenger',
  },
  'rating-1800': {
    name: 'Rival',
    description: 'Reach a rating of 1800 in any bracket.',
    stat: 'rating',
    at: 1800,
    title: 'Rival',
  },
  'rating-2000': {
    name: 'Duelist',
    description: 'Reach a rating of 2000 in any bracket.',
    stat: 'rating',
    at: 2000,
    title: 'Duelist',
  },
  'rating-2200': {
    name: 'Gladiator',
    description: 'Reach a rating of 2200 in any bracket.',
    stat: 'rating',
    at: 2200,
    title: 'Gladiator',
  },
  ...classAchievements,
};

export const ACHIEVEMENT_IDS = Object.keys(ACHIEVEMENTS);
